import { ShiftOpportunityType, UnavailableReasons, useFindShiftsPageMoreLazyQuery, useFindShiftsPageMoreQuery } from '@amzn/atoz-shiftapp-types';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useFindShiftsPageAsyncQueryParams } from './useFindShiftsPageAsyncQueryParams';
export var useFindShiftsApiRedesign = function (selectedDate, shiftOpportunities) {
    var _a, _b, _c, _d;
    var _e = useState([]), lazyShiftOpportunities = _e[0], setLazyShiftOpportunities = _e[1];
    var _f = useState(0), lazyCalls = _f[0], setLazyCalls = _f[1];
    var lsoRef = useRef(lazyShiftOpportunities);
    var lazyCallsRef = useRef(lazyCalls);
    useEffect(function () {
        lsoRef.current = lazyShiftOpportunities;
        lazyCallsRef.current = lazyCalls;
    }, [lazyCalls]);
    var lowerBound = Math.max(0, Math.trunc(selectedDate.diff(DateTime.now().startOf('day'), 'days').days) - 7);
    var _g = useState([lowerBound, lowerBound + 7]), bounds = _g[0], setBounds = _g[1];
    var boundsRef = useRef(bounds);
    useEffect(function () {
        boundsRef.current = bounds;
    }, [bounds]);
    var getMoreLazy = useFindShiftsPageMoreLazyQuery()[0];
    var setRef = function (selector) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useCallback(function (node) {
            if (node) {
                var days_1 = Array.from(node.children || []);
                var observer = new IntersectionObserver(function (entries, observer) {
                    entries.every(function (entry) {
                        if (entry.isIntersecting) {
                            var dayNum_1 = days_1.indexOf(entry.target);
                            if (dayNum_1 < boundsRef.current[0] ||
                                dayNum_1 > boundsRef.current[1]) {
                                var _a = getLazyBounds(boundsRef.current, dayNum_1), start = _a.start, end = _a.end;
                                getMoreLazy({
                                    variables: getFindShiftsOpportunitiesVariables(start, end),
                                    errorPolicy: 'all',
                                    onCompleted: function (data) {
                                        var _a;
                                        lsoRef.current[dayNum_1] =
                                            ((_a = data.shiftOpportunities) === null || _a === void 0 ? void 0 : _a.opportunities) || [];
                                        setLazyShiftOpportunities(lsoRef.current);
                                        setLazyCalls(lazyCallsRef.current + 1);
                                    }
                                });
                                observer.unobserve(entry.target);
                                var newLow = Math.min(boundsRef.current[0], dayNum_1 - 7);
                                var newHigh = Math.max(boundsRef.current[1], dayNum_1 + 7);
                                if (newLow != boundsRef.current[0] &&
                                    newLow > 0)
                                    observer.observe(days_1[newLow - 1]);
                                if (newHigh != boundsRef.current[1] &&
                                    newHigh < days_1.length - 1)
                                    observer.observe(days_1[newHigh + 1]);
                                setBounds([newLow, newHigh]);
                                return false;
                            }
                            else {
                                observer.unobserve(entry.target);
                            }
                        }
                        return true;
                    });
                }, { threshold: 0.25, rootMargin: '16px', root: node });
                if (bounds[0] > 0)
                    observer.observe(days_1[bounds[0] - 1]);
                if (bounds[1] < days_1.length - 1)
                    observer.observe(days_1[bounds[1] + 1]);
            }
            selector.current = node;
        }, []);
    };
    var ranges = useFindShiftsPageAsyncQueryParams(selectedDate);
    var refetches = useRef([]);
    var _h = useFindShiftsPageMoreQuery({
        variables: getFindShiftsOpportunitiesVariables((_a = ranges[0]) === null || _a === void 0 ? void 0 : _a.startTime, (_b = ranges[0]) === null || _b === void 0 ? void 0 : _b.endTime),
        errorPolicy: 'all'
    }), _j = _h.data, _k = _j === void 0 ? {} : _j, shiftOpportunitiesAsync = _k.shiftOpportunities, refetch = _h.refetch;
    var _l = useFindShiftsPageMoreQuery({
        variables: getFindShiftsOpportunitiesVariables((_c = ranges[1]) === null || _c === void 0 ? void 0 : _c.startTime, (_d = ranges[1]) === null || _d === void 0 ? void 0 : _d.endTime),
        errorPolicy: 'all',
        skip: !ranges[1]
    }), _m = _l.data, _o = _m === void 0 ? {} : _m, shiftOpportunitiesAsync2 = _o.shiftOpportunities, refetch2 = _l.refetch;
    useEffect(function () {
        refetches.current.push(refetch);
        if (ranges[1])
            refetches.current.push(refetch2);
    }, [refetch, refetch2]);
    var allShiftOpportunities = useMemo(function () {
        if (shiftOpportunities) {
            return shiftOpportunities
                .concat((shiftOpportunitiesAsync === null || shiftOpportunitiesAsync === void 0 ? void 0 : shiftOpportunitiesAsync.opportunities) || [])
                .concat((shiftOpportunitiesAsync2 === null || shiftOpportunitiesAsync2 === void 0 ? void 0 : shiftOpportunitiesAsync2.opportunities) || [])
                .concat(lsoRef.current.flat());
        }
        return undefined;
    }, [
        shiftOpportunities,
        shiftOpportunitiesAsync,
        shiftOpportunitiesAsync2,
        lazyCalls
    ]);
    return {
        setRef: setRef,
        allShiftOpportunities: allShiftOpportunities,
        refetches: refetches.current
    };
};
export var getLazyBounds = function (bounds, newEl) {
    var lower = newEl < bounds[0] ? Math.max(newEl - 7, 0) : newEl;
    var upper = newEl > bounds[1] ? Math.min(newEl + 7, 70) : newEl;
    var start = DateTime.now()
        .plus({ days: lower })
        .startOf('day')
        .toUTC()
        .toISO();
    var end = DateTime.now()
        .plus({ days: upper })
        .endOf('day')
        .toUTC()
        .toISO();
    return { start: start, end: end };
};
export var getFindShiftsOpportunitiesVariables = function (start, end) {
    return {
        timeRange: {
            start: start,
            end: end
        },
        filter: {
            includeIneligible: true,
            unavailableReasonsToInclude: [
                UnavailableReasons.AssociateAccepted,
                UnavailableReasons.ShiftOpportunityCapacityMet,
                UnavailableReasons.ShiftOpportunityExpired
            ]
        },
        opportunityTypes: {
            types: [
                ShiftOpportunityType.Drop,
                ShiftOpportunityType.VoluntaryTimeOff,
                ShiftOpportunityType.Add,
                ShiftOpportunityType.VoluntaryExtraTime
            ]
        }
    };
};
